/**
 * The realtime gateway client (protocol: packages/protocol/src/gateway.ts).
 *
 * - HELLO, then heartbeats: the first after `interval × jitter`, then every
 *   interval. No ACK before the next beat means a zombie connection: close
 *   it and resume on a new one.
 * - IDENTIFY on a fresh session; RESUME (session id + last seq) after a
 *   drop. RESUMED or READY settles the connection.
 * - RECONNECT (op 7) resumes; INVALID_SESSION (op 9) resumes when `d` is
 *   true, otherwise waits 1-5 s and identifies again.
 * - Drops reconnect with exponential backoff and full jitter. Auth failure
 *   (4004) asks for a fresh token once, then stops.
 *
 * Timers and the WebSocket are injectable, so tests drive it with fake
 * timers and a fake socket.
 */
import {
  GATEWAY_PATH,
  GatewayCloseCodes,
  GatewayOpcodes,
  type GatewayDispatch,
  type GatewayPresenceUpdateData,
  type GatewayReceivePayload,
  type GatewaySendPayload,
  type IdentifyProperties,
} from '@neosocial/protocol';

export type GatewayStatus =
  | 'idle'
  | 'connecting'
  | 'identifying'
  | 'resuming'
  | 'ready'
  | 'reconnecting'
  | 'auth_failed'
  | 'closed';

export interface SocketLike {
  readyState: number;
  send(data: string): void;
  close(code?: number, reason?: string): void;
  onopen: ((ev: unknown) => void) | null;
  onmessage: ((ev: { data: unknown }) => void) | null;
  onclose: ((ev: { code: number; reason?: string }) => void) | null;
  onerror: ((ev: unknown) => void) | null;
}

export interface GatewayOptions {
  /** Base URL, for example "wss://host" (GATEWAY_PATH is appended). */
  url: () => string;
  /** A valid access token; `fresh` asks for a refreshed one (after 4004). */
  token: (fresh?: boolean) => Promise<string | null>;
  properties: IdentifyProperties;
  presence?: () => GatewayPresenceUpdateData | undefined;
  createSocket?: (url: string) => SocketLike;
  setTimeout?: (fn: () => void, ms: number) => unknown;
  clearTimeout?: (id: unknown) => void;
  random?: () => number;
  backoffBaseMs?: number;
  backoffMaxMs?: number;
}

type Listener<T> = (v: T) => void;

const OPEN = 1;
/** Our own close code for a zombie connection (any 4xxx-free code keeps the session). */
export const ZOMBIE_CLOSE_CODE = 3999;

export class GatewayClient {
  status: GatewayStatus = 'idle';
  sessionId: string | null = null;
  seq: number | null = null;
  resumeUrl: string | null = null;
  /** Consecutive failed attempts; reset on READY or RESUMED. */
  attempts = 0;

  private ws: SocketLike | null = null;
  private hbTimer: unknown = null;
  private retryTimer: unknown = null;
  private hbInterval = 0;
  private acked = true;
  private stopped = true;
  private triedFreshToken = false;
  private dispatchListeners: Listener<GatewayDispatch>[] = [];
  private statusListeners: Listener<GatewayStatus>[] = [];
  private readonly st: (fn: () => void, ms: number) => unknown;
  private readonly ct: (id: unknown) => void;
  private readonly rnd: () => number;

  constructor(private readonly o: GatewayOptions) {
    this.st = o.setTimeout ?? ((fn, ms) => globalThis.setTimeout(fn, ms));
    this.ct =
      o.clearTimeout ?? ((id) => globalThis.clearTimeout(id as ReturnType<typeof setTimeout>));
    this.rnd = o.random ?? Math.random;
  }

  onDispatch(fn: Listener<GatewayDispatch>): () => void {
    this.dispatchListeners.push(fn);
    return () => (this.dispatchListeners = this.dispatchListeners.filter((f) => f !== fn));
  }
  onStatus(fn: Listener<GatewayStatus>): () => void {
    this.statusListeners.push(fn);
    return () => (this.statusListeners = this.statusListeners.filter((f) => f !== fn));
  }

  connect(): void {
    this.stopped = false;
    this.open();
  }

  /** Closes for good (sign out). The session is forgotten. */
  close(): void {
    this.stopped = true;
    this.clearTimers();
    const ws = this.ws;
    this.ws = null;
    if (ws) {
      this.detach(ws);
      try {
        ws.close(1000, 'bye');
      } catch {
        /* already closed */
      }
    }
    this.sessionId = null;
    this.seq = null;
    this.setStatus('closed');
  }

  sendPresence(d: GatewayPresenceUpdateData): void {
    this.send({ op: GatewayOpcodes.PRESENCE_UPDATE, d, s: null, t: null });
  }

  /** Backoff for the n-th retry: full jitter between 0 and min(max, base × 2^n), at least 250 ms. */
  backoffMs(n: number): number {
    const base = this.o.backoffBaseMs ?? 1000;
    const max = this.o.backoffMaxMs ?? 30_000;
    const cap = Math.min(max, base * 2 ** Math.min(n, 16));
    return Math.max(250, Math.round(cap * this.rnd()));
  }

  private setStatus(s: GatewayStatus): void {
    if (this.status === s) return;
    this.status = s;
    for (const f of this.statusListeners.slice()) f(s);
  }

  private open(): void {
    if (this.stopped) return;
    this.clearTimers();
    const resuming = !!(this.sessionId && this.seq !== null);
    this.setStatus(this.attempts > 0 ? 'reconnecting' : 'connecting');
    const base = (resuming && this.resumeUrl) || this.o.url();
    const url =
      base.replace(/\/+$/, '') +
      (base.endsWith(GATEWAY_PATH) ? '' : GATEWAY_PATH) +
      '?v=1&encoding=json';
    let ws: SocketLike;
    try {
      ws = this.o.createSocket
        ? this.o.createSocket(url)
        : (new WebSocket(url) as unknown as SocketLike);
    } catch {
      this.scheduleRetry();
      return;
    }
    this.ws = ws;
    ws.onmessage = (ev) => this.onMessage(ws, ev.data);
    ws.onclose = (ev) => this.onClose(ws, ev.code);
    ws.onerror = () => {
      /* onclose follows */
    };
  }

  private detach(ws: SocketLike): void {
    ws.onopen = ws.onmessage = ws.onclose = ws.onerror = null;
  }

  private send(p: GatewaySendPayload): void {
    if (this.ws && this.ws.readyState === OPEN) this.ws.send(JSON.stringify(p));
  }

  private onMessage(ws: SocketLike, raw: unknown): void {
    if (ws !== this.ws) return;
    let p: GatewayReceivePayload;
    try {
      p = JSON.parse(String(raw)) as GatewayReceivePayload;
    } catch {
      return;
    }
    switch (p.op) {
      case GatewayOpcodes.HELLO:
        this.hbInterval = p.d.heartbeat_interval;
        this.startHeartbeat(true);
        void this.identifyOrResume();
        break;
      case GatewayOpcodes.HEARTBEAT_ACK:
        this.acked = true;
        break;
      case GatewayOpcodes.HEARTBEAT:
        this.beat();
        break;
      case GatewayOpcodes.RECONNECT:
        this.drop(4000, true);
        break;
      case GatewayOpcodes.INVALID_SESSION:
        if (!p.d) {
          this.sessionId = null;
          this.seq = null;
          this.resumeUrl = null;
        }
        this.clearTimers();
        this.retryTimer = this.st(
          () => void this.identifyOrResume(),
          p.d ? 0 : 1000 + Math.round(this.rnd() * 4000),
        );
        this.startHeartbeat(false);
        break;
      case GatewayOpcodes.DISPATCH: {
        if (typeof p.s === 'number') this.seq = p.s;
        if (p.t === 'READY') {
          this.sessionId = p.d.session_id;
          this.resumeUrl = p.d.resume_gateway_url || null;
          this.ready();
        } else if (p.t === 'RESUMED') {
          this.ready();
        }
        for (const f of this.dispatchListeners.slice()) f(p);
        break;
      }
    }
  }

  private ready(): void {
    this.attempts = 0;
    this.triedFreshToken = false;
    this.setStatus('ready');
  }

  private async identifyOrResume(): Promise<void> {
    const ws = this.ws;
    const token = await this.o.token(false);
    if (ws !== this.ws || this.stopped) return;
    if (!token) {
      this.setStatus('auth_failed');
      this.stop();
      return;
    }
    if (this.sessionId && this.seq !== null) {
      this.setStatus('resuming');
      this.send({
        op: GatewayOpcodes.RESUME,
        d: { token, session_id: this.sessionId, seq: this.seq },
        s: null,
        t: null,
      });
    } else {
      this.setStatus('identifying');
      this.send({
        op: GatewayOpcodes.IDENTIFY,
        d: { token, properties: this.o.properties, presence: this.o.presence?.() },
        s: null,
        t: null,
      });
    }
  }

  private startHeartbeat(first: boolean): void {
    if (this.hbTimer) this.ct(this.hbTimer);
    this.acked = true;
    const wait = first ? Math.round(this.hbInterval * this.rnd()) : this.hbInterval;
    this.hbTimer = this.st(() => this.tick(), wait);
  }

  private tick(): void {
    if (!this.acked) {
      // zombie: no ACK since the last beat
      this.drop(ZOMBIE_CLOSE_CODE, true);
      return;
    }
    this.beat();
    this.hbTimer = this.st(() => this.tick(), this.hbInterval);
  }

  private beat(): void {
    this.acked = false;
    this.send({ op: GatewayOpcodes.HEARTBEAT, d: this.seq, s: null, t: null });
  }

  /** Closes the current socket ourselves and reconnects. */
  private drop(code: number, resume: boolean): void {
    const ws = this.ws;
    this.ws = null;
    this.clearTimers();
    if (ws) {
      this.detach(ws);
      try {
        ws.close(code === ZOMBIE_CLOSE_CODE ? 4000 : code, 'reconnect');
      } catch {
        /* ignore */
      }
    }
    if (!resume) {
      this.sessionId = null;
      this.seq = null;
    }
    this.scheduleRetry();
  }

  private onClose(ws: SocketLike, code: number): void {
    if (ws !== this.ws) return;
    this.detach(ws);
    this.ws = null;
    this.clearTimers();
    if (this.stopped) return;
    if (code === GatewayCloseCodes.AUTHENTICATION_FAILED) {
      if (this.triedFreshToken) {
        this.setStatus('auth_failed');
        this.stop();
        return;
      }
      this.triedFreshToken = true;
      void this.o.token(true).then((t) => {
        if (this.stopped) return;
        if (!t) {
          this.setStatus('auth_failed');
          this.stop();
        } else this.open();
      });
      return;
    }
    if (
      code === GatewayCloseCodes.INVALID_API_VERSION ||
      code === GatewayCloseCodes.UPDATE_REQUIRED
    ) {
      this.setStatus('closed');
      this.stop();
      return;
    }
    if (code === GatewayCloseCodes.INVALID_SEQ || code === GatewayCloseCodes.SESSION_TIMED_OUT) {
      this.sessionId = null;
      this.seq = null;
    }
    this.scheduleRetry();
  }

  private scheduleRetry(): void {
    if (this.stopped) return;
    const wait = this.backoffMs(this.attempts);
    this.attempts += 1;
    this.setStatus('reconnecting');
    this.retryTimer = this.st(() => this.open(), wait);
  }

  private stop(): void {
    this.stopped = true;
    this.clearTimers();
  }

  private clearTimers(): void {
    if (this.hbTimer) this.ct(this.hbTimer);
    if (this.retryTimer) this.ct(this.retryTimer);
    this.hbTimer = null;
    this.retryTimer = null;
  }
}
